import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getAllCabs, addCab, addCabSimple, updateCab, deleteCab } from '../../features/admin/adminSlice';
import { createPortal } from 'react-dom';

// API URL from centralized config
const { API_URL } = require('../../config/apiConfig');

// Custom CSS for toggle switch
const toggleStyles = `
  .toggle-checkbox {
    right: 0;
    z-index: 5;
    transition-property: transform;
  }
  input:checked + .toggle-label {
    background-color: #10B981;
  }
  input:checked + .toggle-label + .toggle-text:before {
    content: "Active";
  }
  input:not(:checked) + .toggle-label + .toggle-text:before {
    content: "Inactive";
  }
  input:checked + .toggle-label .toggle-checkbox {
    transform: translateX(100%);
    border-color: #ffffff;
  }
  .toggle-label {
    transition-property: background-color;
    transition-duration: 300ms;
  }
  
  /* Slide-over panel keyframes */
  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
  .animate-slide-in {
    animation: slideInFromRight 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
`;

const Cabs = () => {
  const dispatch = useDispatch();
  const { cabs, isLoading, isError, isSuccess, message } = useSelector((state) => state.admin);
  
  // Add the toggle styles to the document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = toggleStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCab, setCurrentCab] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [featureInput, setFeatureInput] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [addOnInput, setAddOnInput] = useState({ name: '', description: '', price: '' });

  // Fetch all cabs on component mount
  useEffect(() => {
    dispatch(getAllCabs());
  }, [dispatch]);
  
  // Show toast messages on success or error (with toastId to prevent duplicates)
  useEffect(() => {
    if (isError) {
      const msg = String(message || "");
      // Suppress unrelated wallet summary errors on this page to avoid duplicate toasts
      if (!/wallet summary/i.test(msg)) {
        toast.error(msg, { toastId: 'admin-error' });
      }
    }

    if (isSuccess && message) {
      const msg = String(message || "");
      // Avoid duplicate success toasts for cab operations (handled by the form flow)
      if (!/\bcab\b/i.test(msg) && !/cab created|cab updated|image uploaded/i.test(msg)) {
        toast.success(msg, { toastId: 'admin-success' });
      }
    }
  }, [isError, isSuccess, message]);
  
  // Form state for creating/editing cabs
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    acType: 'AC',
    seatingCapacity: 4,
    luggageCapacity: 2,
    baseKmPrice: 0,
    extraFarePerKm: 0,
    includedKm: 0,
    waitingChargePerMinute: 0,
    vehicleLocation: '',
    fuelType: 'petrol',
    driverRating: 0,
    fuelCharges: { included: true, amount: 0 },
    driverCharges: { included: true, amount: 0 },
    nightCharges: { included: true, amount: 0 },
    discount: 0,
    features: [],
    addOnServices: [],
    active: true,
    originalImageUrl: ''
  });
  
  // State already declared at the top of the component

  const handleOpenModal = (cab = null) => {
    if (cab) {
      // Debug logging to check cab data
      console.log('Opening modal for cab:', cab);
      console.log('Driver Rating value:', cab.driverRating, 'Type:', typeof cab.driverRating);

      // Store the original cab object to preserve all data including the image URL
      setFormData({
        name: cab.name,
        description: cab.description,
        acType: cab.acType || 'AC',
        seatingCapacity: Number(cab.seatingCapacity) || 4,
        luggageCapacity: Number(cab.luggageCapacity) || 2,
        baseKmPrice: cab.baseKmPrice || '',
        extraFarePerKm: Number(cab.extraFarePerKm) || 0,
        includedKm: Number(cab.includedKm) || 0,
        waitingChargePerMinute: Number(cab.waitingChargePerMinute) || 0,
        vehicleLocation: cab.vehicleLocation || '',
        fuelType: cab.fuelType || 'petrol',
        driverRating: Number(cab.driverRating) || 0,
        features: cab.features || [],
        addOnServices: cab.addOnServices || cab.add_on_services || [],
        fuelCharges: {
          included: cab.fuelCharges?.included !== undefined ? cab.fuelCharges.included : true,
          amount: Number(cab.fuelCharges?.amount) || 0
        },
        driverCharges: {
          included: cab.driverCharges?.included !== undefined ? cab.driverCharges.included : true,
          amount: Number(cab.driverCharges?.amount) || 0
        },
        nightCharges: {
          included: cab.nightCharges?.included !== undefined ? cab.nightCharges.included : true,
          amount: Number(cab.nightCharges?.amount) || 0
        },
        discount: Number(cab.discount) || 0,
        active: cab.active !== undefined ? cab.active : true,
        // Store the original image URL to preserve it if no new image is uploaded
        originalImageUrl: cab.imageUrl || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3ECab Image%3C/text%3E%3C/svg%3E"
      });
      setCurrentCab(cab);
      setImagePreview(cab.imageUrl || '');
    } else {
      setFormData({
        name: '',
        description: '',
        acType: 'AC',
        seatingCapacity: 4,
        luggageCapacity: 2,
        baseKmPrice: '',
        extraFarePerKm: 0,
        includedKm: 0,
        waitingChargePerMinute: 0,
        vehicleLocation: '',
        fuelType: 'petrol',
        driverRating: 0,
        features: [],
        addOnServices: [],
        fuelCharges: {
          included: true,
          amount: 0
        },
        driverCharges: {
          included: true,
          amount: 0
        },
        nightCharges: {
          included: true,
          amount: 0
        },
        discount: 0,
        active: true,
        originalImageUrl: ''
      });
      setCurrentCab(null);
      setImagePreview('');
      setImageFile(null);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentCab(null);
    setFormData({
      name: '',
      description: '',
      acType: 'AC',
      seatingCapacity: 4,
      luggageCapacity: 2,
      baseKmPrice: '',
      extraFarePerKm: 0,
      includedKm: 0,
      waitingChargePerMinute: 0,
      vehicleLocation: '',
      fuelType: 'petrol',
      driverRating: 0,
      features: [],
      addOnServices: [],
      fuelCharges: {
        included: true,
        amount: 0
      },
      driverCharges: {
        included: true,
        amount: 0
      },
      nightCharges: {
        included: true,
        amount: 0
      },
      discount: 0,
      active: true,
      originalImageUrl: ''
    });
    setFeatureInput('');
    setAddOnInput({ name: '', description: '', price: '' });
    setSelectedImage(null);
  };

  // Handle feature input key down (add feature on Enter)
  const handleFeatureKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (featureInput.trim() !== '') {
        if (!formData.features.includes(featureInput.trim())) {
          setFormData({
            ...formData,
            features: [...formData.features, featureInput.trim()]
          });
          setFeatureInput('');
        }
      }
    }
  };

  // Handle adding a feature
  const handleAddFeature = () => {
    if (featureInput.trim() !== '') {
      if (!formData.features.includes(featureInput.trim())) {
        setFormData({
          ...formData,
          features: [...formData.features, featureInput.trim()]
        });
        setFeatureInput('');
      }
    }
  };

  // Handle removing a feature (supports both index and direct feature value)
  const handleRemoveFeature = (featureOrIndex) => {
    if (typeof featureOrIndex === 'number') {
      // If an index was passed
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== featureOrIndex)
      }));
    } else {
      // If a feature string was passed
      setFormData({
        ...formData,
        features: formData.features.filter(f => f !== featureOrIndex)
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle nested charge objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      // Handle regular fields
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value)
      }));
    }
  };

  // Comprehensive handler for image uploads
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setSelectedImage(reader.result); // Also set the selectedImage for preview
      };
      reader.readAsDataURL(file);
    }
  };

  // For backward compatibility with existing code
  const handleFeatureChange = handleFeatureKeyDown;

  // For backward compatibility with existing code
  const removeFeature = handleRemoveFeature;

  // Handle add-on service input change
  const handleAddOnChange = (e) => {
    const { name, value } = e.target;
    setAddOnInput(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle adding a new add-on service
  const handleAddAddOn = () => {
    if (addOnInput.name.trim() && addOnInput.description.trim() && addOnInput.price) {
      const newAddOn = {
        name: addOnInput.name.trim(),
        description: addOnInput.description.trim(),
        price: parseFloat(addOnInput.price) || 0
      };

      setFormData(prev => ({
        ...prev,
        addOnServices: [...prev.addOnServices, newAddOn]
      }));

      setAddOnInput({ name: '', description: '', price: '' });
    }
  };

  // Handle removing an add-on service
  const handleRemoveAddOn = (index) => {
    setFormData(prev => ({
      ...prev,
      addOnServices: prev.addOnServices.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Cab name is required');
      return;
    }
    
    console.log('Submitting form data:', formData);
    // Show a single processing toast for the entire create/update flow
    toast.info('Processing cab... Please wait', { toastId: 'cab-process', autoClose: false });
    
    try {
      const token = localStorage.getItem('token') || 
        (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).token : null);
      
      if (!token) {
        toast.error('Authentication token not found', { toastId: 'auth-error' });
        return;
      }
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Step 1: Create a JSON object with the cab data
      // Check if the name already contains (AC) or (Non-AC)
      let cabName = formData.name;
      if (!cabName.includes('(AC)') && !cabName.includes('(Non-AC)')) {
        cabName = formData.acType === 'Non-AC' 
          ? `${cabName} (Non-AC)` 
          : `${cabName} (AC)`;
      }
      const nameWithAC = cabName;
      
      const cabData = {
        name: nameWithAC,
        description: formData.description || '',
        acType: formData.acType || 'AC',
        seatingCapacity: parseInt(formData.seatingCapacity) || 4,
        luggageCapacity: parseInt(formData.luggageCapacity) || 2,
        baseKmPrice: parseFloat(formData.baseKmPrice) || 0,
        extraFarePerKm: parseFloat(formData.extraFarePerKm) || 0,
        includedKm: parseInt(formData.includedKm) || 0,
        waitingChargePerMinute: parseFloat(formData.waitingChargePerMinute) || 0,
        vehicleLocation: formData.vehicleLocation || '',
        fuelType: formData.fuelType || 'petrol',
        driverRating: parseFloat(formData.driverRating) || 0,
        fuelCharges: {
          included: formData.fuelCharges.included,
          amount: formData.fuelCharges.included ? 0 : parseFloat(formData.fuelCharges.amount) || 0
        },
        driverCharges: {
          included: formData.driverCharges.included,
          amount: formData.driverCharges.included ? 0 : parseFloat(formData.driverCharges.amount) || 0
        },
        nightCharges: {
          included: formData.nightCharges.included,
          amount: formData.nightCharges.included ? 0 : parseFloat(formData.nightCharges.amount) || 0
        },
        active: formData.active,
        features: formData.features || [],
        addOnServices: formData.addOnServices || [],
        discount: parseFloat(formData.discount) || 0,
        // Preserve existing image URL if updating and no new image is uploaded
        imageUrl: formData.directImageUrl || (currentCab && !imageFile ? formData.originalImageUrl : '')
      };
      
      console.log('Step 1: Creating cab with data:', cabData);
      
      let response;
      
      if (currentCab) {
        // Update existing cab
        response = await axios.put(
          `${API_URL}/cab/${currentCab._id}`,
          cabData,
          config
        );
        // Success will be shown after the entire flow completes (including optional image upload)
      } else {
        // Create new cab
        response = await axios.post(
          `${API_URL}/cab/noimage`,
          cabData,
          config
        );
        // Success will be shown after the entire flow completes (including optional image upload)
      }
      
      console.log('Cab created/updated:', response.data);
      
      // Step 2: If there's an image file, upload it separately using the direct endpoint
      if (imageFile) {
        const cabId = response.data.data._id;
        console.log('Step 2: Uploading image for cab:', cabId);
        
        // Create a new FormData for the image only
        const imageFormData = new FormData();
        
        // Add the file with the name 'image' - this is critical
        imageFormData.append('image', imageFile);
        
        // Log FormData contents for debugging
        console.log('FormData created with file:', imageFile.name, 'size:', imageFile.size, 'type:', imageFile.type);
        
        // Update headers for multipart/form-data - don't set Content-Type manually
        // Let Axios set the correct multipart boundary
        const imageConfig = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        try {
          // Add loading indicator
          // Update processing toast to indicate image upload step
          toast.update('cab-process', { render: 'Uploading image...', type: 'info', isLoading: true, autoClose: false });
          
          // Upload the image using the direct endpoint
          const imageResponse = await axios.post(
            `${API_URL}/cab/${cabId}/image`,
            imageFormData,
            imageConfig
          );
          
          console.log('Image uploaded successfully:', imageResponse.data);
          // Update the single processing toast to indicate overall success
          toast.update('cab-process', { render: 'Cab saved and image uploaded successfully', type: 'success', isLoading: false, autoClose: 3000 });

          // Update the cab with the new image URL in the local state if needed
          if (imageResponse.data?.data?.imageUrl) {
            // This could update the UI to show the new image right away
            console.log('Updated image URL:', imageResponse.data.data.imageUrl);
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          console.error('Error details:', imageError.response?.data || 'No response data');

          // More detailed error handling
          if (imageError.response?.status === 500) {
            toast.error(`Server error during image upload: ${imageError.response?.data?.message || 'Unknown error'}`, { toastId: 'upload-failed' });

            // Update the cab to let the user know it was created but image failed
            try {
              await axios.put(
                `${API_URL}/cab/${cabId}`,
                { ...cabData, note: 'Image upload failed - please try again' },
                config
              );
            } catch (e) {
              // Silently fail if this update fails
              console.error('Failed to update cab with note:', e);
            }
          } else if (imageError.response?.status === 400) {
            toast.update('cab-process', { render: `Cab saved but image upload failed: ${imageError.response?.data?.message || 'File validation failed'}`, type: 'warning', isLoading: false, autoClose: 5000 });
          } else {
            toast.update('cab-process', { render: `Cab saved but image upload failed (${imageError.response?.status || 'Unknown error'})`, type: 'warning', isLoading: false, autoClose: 5000 });
          }
        }
      } else {
        console.log('No image file provided');
        // Update the processing toast to success since no image upload is required
        toast.update('cab-process', { render: currentCab ? 'Cab updated successfully' : 'Cab created successfully', type: 'success', isLoading: false, autoClose: 3000 });
      }
      
      // Refresh the cab list
      dispatch(getAllCabs());
      
      // Close the modal
      handleCloseModal();
    } catch (error) {
      console.error('Error saving cab data:', error);
      const errMsg = error.response?.data?.message || error.message || 'Unknown error';
      // Update the processing toast to show the error
      toast.update('cab-process', { render: `Error saving cab: ${errMsg}`, type: 'error', isLoading: false, autoClose: 5000 });
    }
  };

  const toggleStatus = (cabId, currentActive) => {
    // Find the cab to update its status
    const cabToUpdate = cabs.find(cab => cab._id === cabId);
    if (!cabToUpdate) {
      toast.error('Cab not found', { toastId: 'cab-not-found' });
      return;
    }
    
    const newActive = !currentActive;
    
    // Create FormData for the update
    const formDataObj = new FormData();
    formDataObj.append('active', newActive.toString());
    
    // Update status using Redux action
    dispatch(updateCab({
      id: cabId,
      cabData: formDataObj
    }));
  };
  
  // Delete a cab
  const handleDelete = (cabId) => {
    if (!window.confirm('Are you sure you want to delete this cab?')) {
      return;
    }
    
    // Delete cab using Redux action
    dispatch(deleteCab(cabId));
  };

  const filteredCabs = cabs.filter(cab => 
    cab.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-8">
      <div className="flex justify-end mb-6">
        <button 
          onClick={() => handleOpenModal()}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add New Cab
        </button>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="w-full md:w-1/3">
            <label htmlFor="search" className="sr-only">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input
                id="search"
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search cabs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Cabs Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCabs.length > 0 ? (
            filteredCabs.map((cab) => (
              <div key={cab._id} className={`bg-white rounded-lg shadow-md overflow-hidden ${cab.active ? '' : 'opacity-70'}`}>
                <div className="bg-gray-200 h-48 flex items-center justify-center">
                  {cab.imageUrl ? (
                    <img 
                      src={cab.imageUrl} 
                      alt={cab.name} 
                      className="h-full w-full object-cover" 
                      onError={(e) => {
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3ECab Image%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  ) : (
                    <svg className="h-24 w-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h2 className="text-xl font-semibold">{cab.name}</h2>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      cab.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {cab.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{cab.description}</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div>
                      <p className="text-gray-500 text-xs">Base Price</p>
                      <p className="font-medium">₹{cab.baseKmPrice}/km</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Extra KM Charge</p>
                      <p className="font-medium">₹{cab.extraFarePerKm || 0}/km</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Waiting Charge</p>
                      <p className="font-medium">₹{cab.waitingChargePerMinute || 0}/min</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Capacity / Luggage</p>
                      <p className="font-medium">{cab.seatingCapacity} P / {cab.luggageCapacity} B</p>
                    </div>
                  </div>
                  
                  {/* Discount Badge */}
                  {cab.discount > 0 && (
                    <div className="mb-3">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                        {cab.discount}% OFF
                      </span>
                    </div>
                  )}
                  
                  {/* Features */}
                  {cab.features && cab.features.length > 0 && (
                    <div className="mb-4">
                      <p className="text-gray-500 text-xs mb-1">Features</p>
                      <div className="flex flex-wrap gap-1">
                        {cab.features.map((feature, index) => (
                          <span key={index} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Charges information */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {cab.fuelCharges && cab.fuelCharges.included ? (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Fuel Included
                      </span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                        Fuel: ₹{cab.fuelCharges?.amount || 0}
                      </span>
                    )}
                    {cab.driverCharges && cab.driverCharges.included ? (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Driver Included
                      </span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                        Driver: ₹{cab.driverCharges?.amount || 0}
                      </span>
                    )}
                    {cab.nightCharges && cab.nightCharges.included ? (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Night Charges Included
                      </span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                        Night: ₹{cab.nightCharges?.amount || 0}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => handleOpenModal(cab)}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm hover:bg-blue-200"
                    >
                      Edit
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDelete(cab._id)}
                        className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => toggleStatus(cab._id, cab.active)}
                        className={`px-3 py-1 rounded text-sm ${
                          cab.active ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {cab.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              {searchTerm ? 'No cabs matching search criteria' : 'No cabs found'}
            </div>
          )}
        </div>
      )}
      {/* Add/Edit Cab Slide-over Drawer Panel */}
      {modalOpen && createPortal(
        <div className="fixed inset-0 overflow-hidden z-[99999]">
          {/* Backdrop Blur Overlay */}
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300"
            onClick={handleCloseModal}
          />
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-2xl bg-white shadow-2xl flex flex-col h-full animate-slide-in relative border-l border-slate-100">
              
              {/* Header */}
              <div className="px-6 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex justify-between items-center shadow-lg">
                <div>
                  <span className="text-[10px] font-black text-blue-200 tracking-widest uppercase block mb-1">
                    Fleet Management
                  </span>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    {currentCab ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit Cab Specifications
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Add New Cab to Fleet
                      </>
                    )}
                  </h3>
                </div>
                <button 
                  onClick={handleCloseModal}
                  className="text-white/80 hover:text-white hover:bg-white/10 p-2.5 rounded-xl transition duration-150 active:scale-[0.98]"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form Body - Scrollable Container */}
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6 bg-slate-50">
                
                {/* 1. Basic Specifications Card */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                    1. Basic Specifications
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="name" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Cab Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                        placeholder="e.g. Maruti Suzuki Ertiga"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="acType" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        AC Type
                      </label>
                      <select
                        name="acType"
                        id="acType"
                        value={formData.acType}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium bg-white"
                      >
                        <option value="AC">AC</option>
                        <option value="Non-AC">Non-AC</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="fuelType" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Fuel Type
                      </label>
                      <select
                        name="fuelType"
                        id="fuelType"
                        value={formData.fuelType}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium bg-white"
                      >
                        <option value="petrol">Petrol</option>
                        <option value="diesel">Diesel</option>
                        <option value="electric">Electric</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="vehicleLocation" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Vehicle Location / City
                      </label>
                      <input
                        type="text"
                        name="vehicleLocation"
                        id="vehicleLocation"
                        value={formData.vehicleLocation}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                        placeholder="e.g. Delhi NCR"
                      />
                    </div>

                    <div>
                      <label htmlFor="driverRating" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Driver Rating (0-5)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          name="driverRating"
                          id="driverRating"
                          value={formData.driverRating}
                          onChange={handleChange}
                          min="0"
                          max="5"
                          step="0.1"
                          className="w-24 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                        />
                        <span className="text-sm font-bold text-amber-500">
                          {'★'.repeat(Math.round(formData.driverRating || 0))}
                          {'☆'.repeat(5 - Math.round(formData.driverRating || 0))}
                        </span>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="description" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Description
                      </label>
                      <textarea
                        name="description"
                        id="description"
                        rows="3"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                        placeholder="Enter description, passenger guidelines, or vehicle condition info..."
                      />
                    </div>
                  </div>
                </div>

                {/* 2. Seating & Luggage Capacities */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    2. Capacity Details
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="seatingCapacity" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Seating Capacity (Passengers)
                      </label>
                      <input
                        type="number"
                        name="seatingCapacity"
                        id="seatingCapacity"
                        value={formData.seatingCapacity}
                        onChange={handleChange}
                        min="1"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                      />
                    </div>

                    <div>
                      <label htmlFor="luggageCapacity" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Luggage Capacity (Bags)
                      </label>
                      <input
                        type="number"
                        name="luggageCapacity"
                        id="luggageCapacity"
                        value={formData.luggageCapacity}
                        onChange={handleChange}
                        min="0"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                      />
                    </div>
                  </div>
                </div>

                {/* 3. Fares & Pricing Rates */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    3. Pricing Rates
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="baseKmPrice" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Base Rate Per Kilometer (₹) *
                      </label>
                      <input
                        type="number"
                        name="baseKmPrice"
                        id="baseKmPrice"
                        value={formData.baseKmPrice}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="extraFarePerKm" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Extra KM Charge (₹/km)
                      </label>
                      <input
                        type="number"
                        name="extraFarePerKm"
                        id="extraFarePerKm"
                        value={formData.extraFarePerKm}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                      />
                    </div>

                    <div>
                      <label htmlFor="waitingChargePerMinute" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Waiting Charge Per Minute (₹/min)
                      </label>
                      <input
                        type="number"
                        name="waitingChargePerMinute"
                        id="waitingChargePerMinute"
                        value={formData.waitingChargePerMinute}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                        step="0.1"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                      />
                    </div>

                    <div>
                      <label htmlFor="discount" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Discount (%)
                      </label>
                      <input
                        type="number"
                        name="discount"
                        id="discount"
                        value={formData.discount}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                        max="100"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition sm:text-sm font-medium"
                      />
                    </div>
                  </div>
                </div>

                {/* 4. Mandatory Inclusions */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-5">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    4. Inclusions & Daily Surcharges
                  </h4>
                  
                  {/* Fuel Charges Group */}
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-700">Fuel Charges</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="fuelCharges.included"
                          checked={formData.fuelCharges.included}
                          onChange={handleChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                        <span className="ml-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">
                          {formData.fuelCharges.included ? 'Included' : 'Chargeable'}
                        </span>
                      </label>
                    </div>
                    
                    {!formData.fuelCharges.included && (
                      <div className="flex items-center gap-3 pt-2 border-t border-slate-200/50">
                        <label htmlFor="fuelCharges.amount" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Fuel Daily Fee (₹):
                        </label>
                        <input
                          type="number"
                          name="fuelCharges.amount"
                          id="fuelCharges.amount"
                          value={formData.fuelCharges.amount}
                          onChange={handleChange}
                          min="0"
                          className="px-3 py-1.5 border border-slate-200 rounded-lg w-32 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>

                  {/* Driver Charges Group */}
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-700">Driver Allowance</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="driverCharges.included"
                          checked={formData.driverCharges.included}
                          onChange={handleChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                        <span className="ml-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">
                          {formData.driverCharges.included ? 'Included' : 'Chargeable'}
                        </span>
                      </label>
                    </div>
                    
                    {!formData.driverCharges.included && (
                      <div className="flex items-center gap-3 pt-2 border-t border-slate-200/50">
                        <label htmlFor="driverCharges.amount" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Driver Daily Fee (₹):
                        </label>
                        <input
                          type="number"
                          name="driverCharges.amount"
                          id="driverCharges.amount"
                          value={formData.driverCharges.amount}
                          onChange={handleChange}
                          min="0"
                          className="px-3 py-1.5 border border-slate-200 rounded-lg w-32 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>

                  {/* Night Charges Group */}
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-700">Night Surcharges</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="nightCharges.included"
                          checked={formData.nightCharges.included}
                          onChange={handleChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                        <span className="ml-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">
                          {formData.nightCharges.included ? 'Included' : 'Chargeable'}
                        </span>
                      </label>
                    </div>
                    
                    {!formData.nightCharges.included && (
                      <div className="flex items-center gap-3 pt-2 border-t border-slate-200/50">
                        <label htmlFor="nightCharges.amount" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Nightly Surcharge (₹):
                        </label>
                        <input
                          type="number"
                          name="nightCharges.amount"
                          id="nightCharges.amount"
                          value={formData.nightCharges.amount}
                          onChange={handleChange}
                          min="0"
                          className="px-3 py-1.5 border border-slate-200 rounded-lg w-32 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* 5. Cab Features */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    5. Cab Features & Amenities
                  </h4>
                  
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {formData.features.length > 0 ? (
                      formData.features.map((feature, index) => (
                        <div key={index} className="bg-blue-50 border border-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5">
                          {feature}
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(index)}
                            className="text-blue-400 hover:text-blue-700 transition"
                            aria-label={`Remove ${feature}`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-400 italic">No features added. Add amenities below.</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyDown={handleFeatureKeyDown}
                      placeholder="e.g. WiFi, Water Bottles, Charging Points"
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                    />
                    <button 
                      type="button"
                      onClick={handleAddFeature}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 rounded-xl transition text-xs flex items-center justify-center active:scale-[0.98]"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* 6. Add-on Services (Optional Outstation Add-ons) */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    6. Optional Add-on Services
                  </h4>

                  <div className="space-y-2 mb-4">
                    {formData.addOnServices && formData.addOnServices.length > 0 ? (
                      formData.addOnServices.map((service, index) => (
                        <div key={index} className="bg-slate-50 border border-slate-100 p-3 rounded-xl flex items-start justify-between">
                          <div>
                            <p className="font-bold text-slate-800 text-sm">{service.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{service.description}</p>
                            <p className="text-xs font-black text-blue-600 mt-1">Fee: ₹{parseFloat(service.price).toFixed(2)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveAddOn(index)}
                            className="text-slate-400 hover:text-red-600 transition"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-400 italic">No add-on services configured.</p>
                    )}
                  </div>

                  <div className="space-y-3 bg-slate-50 border border-slate-100 p-4 rounded-xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="addOnName" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                          Service Title
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="addOnName"
                          value={addOnInput.name}
                          onChange={handleAddOnChange}
                          placeholder="e.g. Carrier / Roof Luggage Carrier"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs font-medium bg-white"
                        />
                      </div>

                      <div>
                        <label htmlFor="addOnPrice" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                          Service Price (₹)
                        </label>
                        <input
                          type="number"
                          name="price"
                          id="addOnPrice"
                          value={addOnInput.price}
                          onChange={handleAddOnChange}
                          placeholder="e.g. 300"
                          min="0"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs font-medium bg-white"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label htmlFor="addOnDescription" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                          Service Details
                        </label>
                        <input
                          type="text"
                          name="description"
                          id="addOnDescription"
                          value={addOnInput.description}
                          onChange={handleAddOnChange}
                          placeholder="e.g. Mandatory carrier for safety guidelines"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs font-medium bg-white"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleAddAddOn}
                      className="w-full bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2 rounded-xl text-xs transition active:scale-[0.98] flex items-center justify-center gap-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Service Item
                    </button>
                  </div>
                </div>

                {/* 7. Image & Availability Status */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    7. Cab Image & Status
                  </h4>

                  {/* Availability Status */}
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
                    <div>
                      <span className="text-sm font-bold text-slate-700 block">Fleet Active Status</span>
                      <span className="text-xs text-slate-400 block mt-0.5">Toggle vehicle display visibility</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">
                        {formData.active ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                  </div>

                  {/* Image Selector & Upload */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        Upload Vehicle Image File
                      </label>
                      <div className="border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center hover:border-blue-500 transition duration-200 cursor-pointer bg-slate-50 relative group">
                        <input 
                          type="file" 
                          id="file-upload" 
                          name="file-upload" 
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={handleImageChange} 
                        />
                        <svg className="mx-auto h-12 w-12 text-slate-400 group-hover:text-blue-500 transition duration-200" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <p className="text-xs font-bold text-slate-600 mt-2">Click or drag image file here</p>
                        <p className="text-[10px] text-slate-400 mt-1">PNG, JPG, GIF, WebP (Max 5MB)</p>
                      </div>
                    </div>

                    {/* Image URL fallback input */}
                    <div>
                      <label htmlFor="directImageUrl" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Or Paste Direct Image URL
                      </label>
                      <input
                        type="url"
                        name="directImageUrl"
                        id="directImageUrl"
                        value={formData.directImageUrl || ''}
                        onChange={(e) => setFormData({ ...formData, directImageUrl: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs font-medium bg-white"
                        placeholder="https://example.com/cab.jpg"
                      />
                    </div>

                    {/* Previews */}
                    {(imagePreview || formData.originalImageUrl || formData.directImageUrl) && (
                      <div className="border border-slate-100 rounded-2xl p-4 bg-slate-50 flex flex-col items-center">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Image Preview</span>
                        <div className="w-full h-40 overflow-hidden rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center">
                          <img 
                            src={imagePreview || formData.directImageUrl || formData.originalImageUrl} 
                            alt="Vehicle Preview" 
                            className="h-full w-full object-contain p-2" 
                            onError={(e) => {
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3EInvalid Image URL%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>

              {/* Fixed Footer */}
              <div className="px-6 py-4 bg-white border-t border-slate-100 flex justify-end gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl text-xs hover:shadow-lg hover:shadow-blue-500/10 transition active:scale-[0.98] flex items-center gap-1.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {currentCab ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    )}
                  </svg>
                  {currentCab ? 'Update Cab Specifications' : 'Add Cab to Fleet'}
                </button>
              </div>

            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Cabs;
